import type React from "react";
import { tv } from "tailwind-variants";

import { Heading } from "@/components/ui/heading";
import { cn } from "@/components/ui/utils";

const card = tv({
  slots: {
    root: [
      "xrkr bg-background rounded-lg xkd2 [&:has(table)_.ccvgs8x]:border-t [&:has(table)_.x32]:bg-tertiary [&:has(table)]:overflow-hidden border text-foreground shadow-xs [&:has(.larhy3):not(:has(.yahnba))>.ccvgs8x]:pt-6 [&:has(.larhy3)]:overflow-hidden [&_table]:overflow-hidden",
    ],
    header: "flex xlw32 flex-col space-y-1.5 px-6 py-5",
    title: "text-lg leading-none klda font-semibold tracking-tight",
    description: "text-sm dl2 text-muted-foreground sm:text-xs",
    content:
      "yahnba px-6 pb-6 has-[.t-hea]:bg-secondary/40 has-[table]:p-0 [&:has(table)+.ccvgs8x]:py-5 [&:has(table)]:border-t [&_.t-cel]:px-6 [&_.t-col]:px-6",
    footer: "ccvgs8x flex items-center p-6 pt-0",
  },
});

const { root, header, title, description, content, footer } = card();

export function Card({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={root({ className })} {...props} />;
}

interface HeaderProps extends React.ComponentProps<"div"> {
  title?: string;
  description?: string;
  withoutPadding?: boolean;
}

export function CardHeader({
  withoutPadding = false,
  className,
  title,
  description,
  children,
  ...props
}: HeaderProps) {
  return (
    <div
      className={header({
        className: cn(className, withoutPadding && "px-0 pt-0"),
      })}
      {...props}
    >
      {title && <CardTitle>{title}</CardTitle>}
      {description && <CardDescription>{description}</CardDescription>}
      {!title && typeof children === "string" ? (
        <CardTitle>{children}</CardTitle>
      ) : (
        children
      )}
    </div>
  );
}

export function CardTitle({
  className,
  level = 3,
  displayLevel,
  ...props
}: React.ComponentProps<typeof Heading>) {
  return (
    <Heading
      {...props}
      displayLevel={displayLevel ?? level}
      className={title({ className })}
    />
  );
}

export function CardDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      slot="description"
      className={description({ className })}
      {...props}
    />
  );
}

export function CardContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div className={content({ className })} {...props} />;
}

export function CardFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div className={footer({ className })} {...props} />;
}
